import {
  forwardRef,
  type ComponentPropsWithRef,
  type ElementType,
  type ForwardRefExoticComponent,
  type ForwardRefRenderFunction,
  type ReactElement,
} from 'react';

type GenericObject = Record<string | number | symbol, unknown>;

/**
 * Utility type that applies Omit distributively over union types.
 * This ensures that when T is a union, Omit is applied to each member separately.
 */
type OmitDistributed<
  TypeToModify,
  KeysToRemove extends keyof GenericObject,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
> = TypeToModify extends any ? Omit<TypeToModify, KeysToRemove> : never;

/**
 * Merges two object types by removing overlapping keys from the first type
 * and combining it with the second type.
 */
type MergeTypes<BaseType, OverrideType> = Omit<BaseType, keyof OverrideType> &
  OverrideType;

/**
 * Distributive version of MergeTypes that works correctly with union types.
 */
type MergeTypesDistributed<BaseType, OverrideType> = OmitDistributed<
  BaseType,
  keyof OverrideType
> &
  OverrideType;

/**
 * Combines the component's custom props with the intrinsic HTML element props,
 * ensuring the 'as' prop is available for polymorphic behavior.
 *
 * @template TargetElement - The HTML element or component type to render as
 * @template CustomProps - The component's own custom properties
 * @template IntrinsicProps - The native HTML element properties
 */
export type PolymorphicComponentPropsWithoutRef<
  TargetElement extends ElementType,
  CustomProps extends object,
  IntrinsicProps extends object,
> = MergeTypesDistributed<IntrinsicProps, CustomProps & { as?: TargetElement }>;

/**
 * Represents the function signature of a polymorphic component that accepts refs.
 * This allows the component to be called with different element types via the 'as' prop.
 *
 * @template DefaultElement - The default element type when 'as' is not provided
 * @template ComponentProps - The component's custom props
 * @template AllowedElements - Constraint for which elements can be used with 'as'
 */
export type PolymorphicComponentType<
  DefaultElement extends AllowedElements,
  ComponentProps extends object = Record<string, never>,
  AllowedElements extends ElementType = ElementType,
> = <TargetElement extends AllowedElements = DefaultElement>(
  props: PolymorphicComponentPropsWithoutRef<
    TargetElement,
    ComponentProps,
    ComponentPropsWithRef<TargetElement>
  >,
) => ReactElement | null;

/**
 * The complete type for a polymorphic component created with forwardRef.
 * This combines the ForwardRefExoticComponent properties with the polymorphic
 * component function signature.
 *
 * @template DefaultElement - The default element type when 'as' is not provided
 * @template ComponentProps - The component's custom props
 * @template AllowedElements - Constraint for which elements can be used with 'as'
 */
export type PolymorphicComponent<
  DefaultElement extends AllowedElements,
  ComponentProps extends object = Record<string, never>,
  AllowedElements extends ElementType = ElementType,
> = MergeTypes<
  ForwardRefExoticComponent<
    MergeTypes<
      ComponentPropsWithRef<DefaultElement>,
      ComponentProps & { as?: DefaultElement }
    >
  >,
  PolymorphicComponentType<DefaultElement, ComponentProps, AllowedElements>
>;

/**
 * Type signature for the polymorphicForwardRef helper function.
 * Transforms a standard forwardRef render function into a fully-typed polymorphic component.
 */
type PolymorphicForwardRefType = <
  DefaultElement extends AllowedElements,
  ComponentProps extends object = Record<string, never>,
  AllowedElements extends ElementType = ElementType,
>(
  renderFunction: ForwardRefRenderFunction<
    unknown,
    ComponentProps & { as?: AllowedElements }
  >,
) => PolymorphicComponent<DefaultElement, ComponentProps, AllowedElements>;

/**
 * Creates a polymorphic component that can render as different HTML elements
 * while maintaining full TypeScript type safety for props and refs.
 *
 * This utility wraps React's forwardRef and adds type-safe support for the 'as' prop,
 * allowing components to be rendered as any HTML element or React component while
 * preserving correct prop types and ref types for the target element.
 *
 * @example
 * ```tsx
 * type TagProps = {
 *   selected?: boolean;
 *   onClose?: () => void;
 * };
 *
 * const Tag = polymorphicForwardRef<'label', TagProps>(
 *   ({ selected, onClose, as, ...props }, ref) => {
 *     const Element = as || 'label';
 *     return <Element ref={ref} {...props} />;
 *   }
 * );
 *
 * // Usage examples:
 * <Tag selected>Label Tag</Tag>
 * <Tag as="button" type="button" onClick={handler}>Button Tag</Tag>
 * <Tag as="a" href="/link">Link Tag</Tag>
 * ```
 */
export const polymorphicForwardRef = forwardRef as PolymorphicForwardRefType;
